import {createSelector} from "reselect";

import {groupSelector} from "@atg-sport-shared/sportsbook-data-access-group";
import {getSportRoute} from "@atg-sport-shared/sportsbook-data-access-route";

import type {Event, EventState} from "@atg-sport-shared/sportsbook-types-event";
import type {GroupMap} from "@atg-sport-shared/sportsbook-types-group";
import type {
    GlobalSportState,
    GlobalState,
} from "@atg-sport-shared/sportsbook-types-root";

const getProps = (_: GlobalSportState, props: number) => props;

export const eventsSelector = (state: GlobalSportState): EventState =>
    state.sportsbook ? state.sportsbook.events : {};

const getEventId = (state: GlobalSportState, eventId: number): number => eventId;

export const eventsArraySelector: (state: GlobalSportState) => Array<Event> =
    createSelector(
        eventsSelector,
        (events: EventState): Array<Event> =>
            events ? Object.keys(events).map((key) => events[key]) : [],
    );

export const liveEventsSelector: (state: GlobalSportState) => Array<Event> =
    createSelector(
        eventsArraySelector,
        (events): Array<Event> =>
            events.filter(
                (event) => event.startTimestamp && event.startTimestamp < Date.now(),
            ),
    );

export const liveEventIdsSelector: (state: GlobalSportState) => Array<number> =
    createSelector(
        liveEventsSelector,
        (events): Array<number> => events.map((event) => event.id),
    );

export const getNonLiveEvents: (state: GlobalSportState) => Array<Event> = createSelector(
    eventsArraySelector,
    (events): Array<Event> =>
        events.filter(
            (event) => event.startTimestamp && event.startTimestamp > Date.now(),
        ),
);

export const createEventSelector: () => (
    state: GlobalSportState,
    eventId: number,
) => Event | null | undefined = () =>
    createSelector(
        eventsSelector,
        getEventId,
        (events: EventState, eventId: number): Event | null | undefined =>
            events ? events[`${eventId}`] : undefined,
    );

export const getSportFromEventId: (
    state: GlobalSportState,
    id: number,
) => string | undefined = createSelector(
    eventsSelector,
    groupSelector,
    getProps,
    (events: EventState, groups: GroupMap, id: number) =>
        (events &&
            events[id] &&
            events[id].pathIds &&
            groups[events[id].pathIds![0]]?.termKey?.toLowerCase()) ||
        undefined,
);

export const createRouteEventSelector: () => (
    state: GlobalState,
) => Event | null | undefined = () =>
    createSelector(eventsSelector, getSportRoute, (events: EventState, route) =>
        route?.eventId && events ? events[`${route.eventId}`] : undefined,
    );

export const getRouteEventSportName: () => (
    state: GlobalState,
) => string | null | undefined = () =>
    createSelector(eventsSelector, getSportRoute, (events: EventState, route) => {
        if (!route?.eventId || !events) return undefined;
        return events[route?.eventId] && events[route?.eventId].sportName;
    });

export const getRouteEventSportTermKey = (state: GlobalState) => {
    const route = getSportRoute(state);
    if (!route?.eventId) return undefined;
    return getSportFromEventId(state, parseInt(route?.eventId, 10));
};

export const getRouteEventStartTimestamp: (state: GlobalState) => number | undefined =
    createSelector(eventsSelector, getSportRoute, (eventsMap: EventState, route) => {
        if (!route?.eventId || !eventsMap) return undefined;
        return (
            (eventsMap[route?.eventId] && eventsMap[route?.eventId].startTimestamp) ||
            undefined
        );
    });

export const makeEventSelector: () => (
    state: GlobalSportState,
    eventId: number,
) => Event | null | undefined = () =>
    createSelector(eventsSelector, getProps, (events, id) => {
        try {
            return events && events[String(id)];
        } catch (e: unknown) {
            return null;
        }
    });
